/**
 * Read access to the durable evidence-lineage endpoints (R-1.5c).
 *
 * Powers the §17 lineage-expansion UI: given a delivered value's `atom_id`,
 * fetch the full cross-run derivation DAG (`evidence_atom_edges`). All network
 * access goes through `apiFetch` (never raw `fetch`); responses are validated at
 * the boundary and a bad payload degrades to `null` so the caller can fall back
 * to the in-payload derivation tree rather than surfacing an error.
 */

import {
	type EvidenceLineage,
	parseEvidenceLineage,
} from "../types/evidenceLineage";
import { apiFetch } from "../utils/apiClient";

class EvidenceService {
	/**
	 * Fetch the derivation DAG rooted at `atomId`. Returns `null` on any
	 * failure (network, non-2xx, malformed payload) — lineage is a progressive
	 * enhancement over the citation the UI already holds.
	 */
	async getAtomLineage(
		atomId: string,
		depth = 32,
	): Promise<EvidenceLineage | null> {
		try {
			const params = new URLSearchParams({ depth: String(depth) });
			const resp = await apiFetch(
				`/api/v3/evidence/atoms/${encodeURIComponent(atomId)}/lineage?${params}`,
			);
			if (!resp.ok) return null;
			return parseEvidenceLineage(await resp.json());
		} catch {
			return null;
		}
	}
}

export const evidenceService = new EvidenceService();
export default evidenceService;
